  // src/pages/Wishlist/Wishlist.jsx - YEH WAISA HI RAHEGA, KOI CHANGE NAHI
  import React, { useState, useEffect, useCallback } from "react";
  import { useWishlist } from "../Context/WishlistContext";
  import { useCart } from "../Context/CartContext";
  import { useCurrency } from "../Context/CurrencyContext";
  import { useNavigate } from "react-router-dom";
  import "./Wishlist.css";

  const Wishlist = () => {
    const { 
      wishlistItems, 
      wishlistCount,
      removeFromWishlist, 
      clearWishlist,
      loading: wishlistLoading,
      apiAvailable,
      showApiWarning,
      error,
      refreshWishlist
    } = useWishlist();
    
    const { addToCart } = useCart();
    const { formatPrice } = useCurrency();
    const navigate = useNavigate();
    
    const [loadingProductId, setLoadingProductId] = useState(null);
    const [localLoading, setLocalLoading] = useState(true);
    const [actionError, setActionError] = useState(null);
    const [actionSuccess, setActionSuccess] = useState(null);
    const [removingProductId, setRemovingProductId] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [optimisticItems, setOptimisticItems] = useState([]);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isCheckingLogin, setIsCheckingLogin] = useState(true);

    const DEFAULT_IMAGE = "/images/dailyroutine1.webp";
    const DEFAULT_IMAGE_2 = "/images/dailyroutine2.webp";
    // Check login status on component mount
    useEffect(() => {
      const checkLoginStatus = () => {
        const token = localStorage.getItem("token");
        const loggedIn = localStorage.getItem("isLoggedIn") === "true";
        
        if (token && loggedIn) {
          setIsLoggedIn(true);
        } else {
          setIsLoggedIn(false);
          localStorage.setItem("redirectAfterLogin", "/wishlist");
        }
        setIsCheckingLogin(false);
      };
      
      checkLoginStatus();
      
      const handleStorageChange = () => {
        checkLoginStatus();
      };
      
      window.addEventListener('storage', handleStorageChange);
      return () => window.removeEventListener('storage', handleStorageChange);
    }, [navigate]);

    // Initialize optimistic items with current wishlist items
    useEffect(() => {
      if (wishlistItems && wishlistItems.length > 0) {
        setOptimisticItems(wishlistItems);
      } else {
        setOptimisticItems([]);
      }
    }, [wishlistItems]);

    const getImageSource = useCallback((apiImage, fallback = DEFAULT_IMAGE) => {
      if (!apiImage) return fallback;
      
      const strUrl = String(apiImage).trim();
      if (strUrl !== "" && strUrl !== "null" && strUrl !== "undefined" && 
          !strUrl.includes('undefined') && !strUrl.includes('null')) {
        return apiImage;
      }
      
      return fallback;
    }, []);

    const showSuccess = useCallback((message) => {
      setActionSuccess(message);
      setTimeout(() => setActionSuccess(null), 3000);
    }, []);

    const showError = useCallback((message) => {
      setActionError(message);
      setTimeout(() => setActionError(null), 5000);
    }, []);

    useEffect(() => {
      const timer = setTimeout(() => {
        setLocalLoading(false);
      }, 1000);
      
      return () => clearTimeout(timer);
    }, []);

    // Handle add to cart
    const handleAddToCart = async (product) => {
      if (!isLoggedIn) {
        localStorage.setItem("redirectAfterLogin", "/wishlist");
        navigate("/login");
        return;
      }
      
      const productId = product.id || product.productId;
      setLoadingProductId(productId);
      setActionError(null);
      
      try {
        const cartItem = {
          id: productId,
          title: product.title || product.name,
          price: typeof product.price === 'string' ? 
                parseInt(product.price.replace(/[^\d.-]/g, '')) : 
                product.price || product.priceValue || 0,
          image: getImageSource(product.image || product.img1, DEFAULT_IMAGE_2),
          brand: product.brand || "",
          inStock: product.inStock,
          selectedSize: "default",
          selectedFragrance: "default",
          quantity: 1,
          basePrice: product.price || product.priceValue || 0,
          baseCurrency: 'USD'
        };
        
        await addToCart(cartItem, 1);
        showSuccess(`${product.title || product.name} added to cart successfully!`);
        
      } catch (error) {
        console.error("Error adding to cart:", error);
        showError("Failed to add item to cart. Please try again.");
      } finally {
        setLoadingProductId(null);
      }
    };

    // Handle remove item
    const handleRemoveItem = async (wishlistItem) => {
      if (!isLoggedIn) {
        localStorage.setItem("redirectAfterLogin", "/wishlist");
        navigate("/login");
        return;
      }
      
      if (!wishlistItem) {
        showError("Item data is missing");
        return;
      }
      
      const wishlistItemId = wishlistItem.id;
      const productId = wishlistItem.productId;
      
      if (!wishlistItemId && !productId) {
        showError("Item ID is missing");
        return;
      }
      
      setRemovingProductId(wishlistItemId || productId);
      setActionError(null);
      
      const itemToRemove = optimisticItems.find(item => 
        item.id === wishlistItemId || 
        item.productId === productId
      );
      
      if (itemToRemove) {
        const newOptimisticItems = optimisticItems.filter(item => 
          item.id !== itemToRemove.id && 
          item.productId !== itemToRemove.productId
        );
        setOptimisticItems(newOptimisticItems);
      }
      
      try {
        const result = await removeFromWishlist(wishlistItemId || productId);
        
        if (result === false || (result && result.success === false)) {
          setOptimisticItems(wishlistItems);
          throw new Error(result?.message || "Failed to remove from server");
        }
        
        showSuccess("Item removed from wishlist");
        
        setTimeout(async () => {
          try {
            await refreshWishlist();
          } catch (refreshError) {
            console.error("Error refreshing wishlist:", refreshError);
          }
        }, 300);
        
      } catch (err) {
        console.error("Error removing from wishlist:", err);
        showError(err.message || "Failed to remove item. Please try again.");
        setOptimisticItems(wishlistItems);
      } finally {
        setTimeout(() => {
          setRemovingProductId(null);
        }, 500);
      }
    };

    // Handle clear wishlist
    const handleClearWishlist = async () => {
      if (!isLoggedIn) {
        localStorage.setItem("redirectAfterLogin", "/wishlist");
        navigate("/login");
        return;
      }
      
      if (!optimisticItems || optimisticItems.length === 0) return;
      
      if (!window.confirm("Are you sure you want to clear your entire wishlist?")) {
        return;
      }
      
      setRefreshing(true);
      setActionError(null);
      
      setOptimisticItems([]);
      
      try {
        const result = await clearWishlist();
        
        if (result === false || (result && result.success === false)) {
          setOptimisticItems(wishlistItems);
          throw new Error(result?.message || "Failed to clear wishlist");
        }
        
        showSuccess("Wishlist cleared successfully");
        
        setTimeout(async () => {
          try {
            await refreshWishlist();
          } catch (refreshError) {
            console.error("Error refreshing wishlist:", refreshError);
          }
        }, 300);
        
      } catch (err) {
        console.error("Error clearing wishlist:", err);
        showError(err.message || "Failed to clear wishlist. Please try again.");
        setOptimisticItems(wishlistItems);
      } finally {
        setRefreshing(false);
      }
    };

    // Handle refresh
    const handleRefresh = async () => {
      if (!isLoggedIn) {
        localStorage.setItem("redirectAfterLogin", "/wishlist");
        navigate("/login");
        return;
      }
      
      setRefreshing(true);
      setActionError(null);
      
      try {
        await refreshWishlist();
        setOptimisticItems(wishlistItems);
        showSuccess("Wishlist refreshed successfully");
      } catch (err) {
        console.error("Error refreshing wishlist:", err);
        showError("Failed to refresh wishlist. Please try again.");
      } finally {
        setRefreshing(false);
      }
    };

    const handleProductClick = (productId) => {
      if (!productId) return;
      navigate(`/product/${productId}`);
    };

    const handleRemoveClick = (item, e) => {
      e.stopPropagation();
      handleRemoveItem(item);
    };

    const handleContinueShopping = () => {
      navigate("/bestsellers");
    };

    const handleLoginRedirect = () => {
      localStorage.setItem("redirectAfterLogin", "/wishlist");
      navigate("/login");
    };

    const displayItems = optimisticItems.length > 0 ? optimisticItems : wishlistItems;
    const itemCount = displayItems?.length || 0;

    // Show loading
    if (isCheckingLogin || (localLoading && wishlistLoading)) {
      return (
        <div className="wishlist-page-container">
          <div className="wishlist-page">
            <h2 className="page-heading">My Wishlist</h2>
            <div className="wishlist-loading">
              <div className="spinner"></div>
              <p>Loading...</p>
            </div>
          </div>
        </div>
      );
    }

    // If not logged in
    if (!isLoggedIn) {
      return (
        <div className="wishlist-page-container">
          <div className="wishlist-page">
            <h2 className="page-heading">My Wishlist</h2>
            <div className="wishlist-empty">
              <div className="empty-icon">
                <svg 
                  width="64" 
                  height="64" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="#cccccc" 
                  strokeWidth="1"
                >
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                </svg>
              </div>
              <p className="empty-text">
                Please login to view your wishlist
              </p>
              <p className="empty-subtext">
                Save items you love by clicking the heart icon on any product.
              </p>
              <div className="empty-actions">
                <button 
                  className="continue-shopping-btn"
                  onClick={handleLoginRedirect}
                >
                  Login to Continue
                </button>
                <button 
                  className="continue-shopping-btn secondary"
                  onClick={handleContinueShopping}
                >
                  Continue Shopping
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Show wishlist
    return (
      <div className="wishlist-page-container">
        <div className="wishlist-page">
          <h2 className="page-heading">My Wishlist</h2>
          
          {/* API Warning */}
          {showApiWarning && (
            <div className="wishlist-alert warning">
              <span className="alert-icon">⚠️</span>
              <span className="alert-text">
                {error || "Wishlist API is currently unavailable. Using local storage."}
              </span>
            </div>
          )}
          
          {/* Status Messages */}
          {actionSuccess && (
            <div className="wishlist-alert success">
              <span className="alert-icon">✓</span>
              <span className="alert-text">{actionSuccess}</span>
              <button className="alert-close" onClick={() => setActionSuccess(null)}>×</button>
            </div>
          )}
          
          {actionError && (
            <div className="wishlist-alert error">
              <span className="alert-icon">!</span>
              <span className="alert-text">{actionError}</span>
              <button className="alert-close" onClick={() => setActionError(null)}>×</button>
            </div>
          )}
          
          {itemCount > 0 ? (
            <div className="wishlist-container">
              <div className="wishlist-header">
                <div className="wishlist-header-left">
                  <p className="wishlist-count">
                    {itemCount} {itemCount === 1 ? 'item' : 'items'}
                  </p>
                  {showApiWarning && (
                    <span className="offline-badge">Offline Mode</span>
                  )}
                </div>
                <div className="wishlist-header-right">
                  <button 
                    className="refresh-wishlist-btn"
                    onClick={handleRefresh}
                    disabled={wishlistLoading || refreshing}
                  >
                    {refreshing ? (
                      <>
                        <span className="spinner small"></span>
                        Refreshing...
                      </>
                    ) : (
                      "Refresh"
                    )}
                  </button>
                  <button 
                    className="clear-wishlist-btn"
                    onClick={handleClearWishlist}
                    disabled={itemCount === 0 || wishlistLoading || refreshing}
                  >
                    {refreshing ? (
                      <>
                        <span className="spinner small"></span>
                        Clearing...
                      </>
                    ) : (
                      "Clear All"
                    )}
                  </button>
                </div>
              </div>
              
              <div className="wishlist-grid">
                {displayItems.map((item, index) => {
                  const img1 = getImageSource(item.image || item.img1, DEFAULT_IMAGE);
                  const img2 = getImageSource(item.img2 || item.img1, DEFAULT_IMAGE);
                  const productId = item.productId || item.id;
                  const wishlistItemId = item.id;
                  const isRemoving = removingProductId === wishlistItemId || removingProductId === productId;
                  const isAddingToCart = loadingProductId === productId;
                  
                  return (
                    <div 
                      key={wishlistItemId || `wishlist-item-${index}`} 
                      className={`wishlist-item ${isRemoving ? 'removing' : ''}`}
                    >
                      <div 
                        className="wishlist-img-box"
                        onClick={() => handleProductClick(productId)}
                      >
                        <img 
                          src={img1} 
                          className="img-main" 
                          alt={item.title || item.name} 
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = DEFAULT_IMAGE_2;
                          }}
                        />
                        <img 
                          src={img2} 
                          className="wishlist-img-hover" 
                          alt={item.title || item.name} 
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = DEFAULT_IMAGE_2;
                          }}
                        />
                        
                        <button 
                          className={`remove-wishlist-btn ${isRemoving ? 'removing' : ''}`}
                          onClick={(e) => handleRemoveClick(item, e)}
                          title="Remove from wishlist"
                          disabled={wishlistLoading || isRemoving || refreshing}
                        >
                          {isRemoving ? (
                            <span className="spinner small"></span>
                          ) : (
                            <svg 
                              width="16" 
                              height="16" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="currentColor" 
                              strokeWidth="2"
                            >
                              <path d="M18 6L6 18M6 6l12 12"></path>
                            </svg>
                          )}
                        </button>
                        
                        <button 
                          className={`wishlist-hover-btn ${
                            isAddingToCart ? 'loading' : ''
                          } ${item.inStock === false ? 'out' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (item.inStock !== false) {
                              handleAddToCart(item);
                            }
                          }}
                          disabled={
                            isAddingToCart || 
                            item.inStock === false ||
                            wishlistLoading ||
                            refreshing
                          }
                        >
                          {isAddingToCart ? (
                            <>
                              <span className="spinner"></span>
                              Adding...
                            </>
                          ) : item.inStock === false ? (
                            "OUT OF STOCK"
                          ) : (
                            "ADD TO CART"
                          )}
                        </button>
                      </div>
                      
                      <div className="wishlist-product-info">
                        <p className="wishlist-brand">{item.brand || "Unknown Brand"}</p>
                        <h4 
                          onClick={() => handleProductClick(productId)}
                        >
                          {item.title || item.name || "Unnamed Product"}
                        </h4>
                        <p className="wishlist-price">{formatPrice(item.price || item.priceValue)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="wishlist-footer">
                <button 
                  className="continue-shopping-btn"
                  onClick={handleContinueShopping}
                  disabled={refreshing}
                >
                  Continue Shopping
                </button>
              </div>
            </div>
          ) : (
            <div className="wishlist-empty">
              <div className="empty-icon">
                <svg 
                  width="64" 
                  height="64" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="#cccccc" 
                  strokeWidth="1"
                >
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                </svg>
              </div>
              <p className="empty-text">
                Your wishlist is currently empty.
              </p>
              <p className="empty-subtext">
                Save items you love by clicking the heart icon on any product.
              </p>
              <div className="empty-actions">
                <button 
                  className="continue-shopping-btn"
                  onClick={handleContinueShopping}
                >
                  Continue Shopping
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  export default Wishlist;